import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import DisplayUploadSuccessModal from '../DisplayUploadSuccessModal';

// Import our extracted components
import BasicInfoSection from './BasicInfoSection';
import LocationSection from './LocationSection';
import MediaUploadSection from './MediaUploadSection';
import SongsSection from './SongsSection';
import { UnifiedScheduleSection } from '@/components/displays/schedule';

// Import our custom hooks
import { useDisplayForm, DisplaySchedule, DisplaySong } from './hooks/useDisplayForm';
import { useMediaUpload } from './hooks/useMediaUpload';

const EditDisplayForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<{ displayId: string; title: string } | null>(null);

  const handleSuccess = (displayId: string, displayData: any) => {
    setSuccessData({
      displayId,
      title: displayData.title
    });
    setShowSuccessModal(true);
  };

  const {
    form,
    setForm,
    isLoading,
    latitude,
    longitude,
    setLatitude,
    setLongitude,
    handleSubmit
  } = useDisplayForm(id, handleSuccess);

  const { uploadingImages, uploadImages, deleteImage } = useMediaUpload();

  // Extract city and state from location for timezone detection
  const [city, setCity] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);

  // Parse city/state from location string when it changes
  useEffect(() => {
    if (form.location) {
      // Try to extract city and state from address
      // Format is typically: "Street, City, State ZIP, Country"
      const parts = form.location.split(',').map(p => p.trim());
      if (parts.length >= 3) {
        setCity(parts[parts.length - 3]);
        // State might have ZIP, extract just state name
        const stateWithZip = parts[parts.length - 2];
        const stateMatch = stateWithZip.match(/^([A-Za-z\s]+)/);
        if (stateMatch) {
          setState(stateMatch[1].trim());
        }
      }
    }
  }, [form.location]);

  // Handler functions for form sections
  const handleBasicInfoChange = (field: string, value: string | string[]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (location: string, coordinates?: { lat: number; lng: number }) => {
    setForm(prev => ({ ...prev, location }));
    if (coordinates) {
      setLatitude(coordinates.lat);
      setLongitude(coordinates.lng);
    }
  };

  const handleImagesChange = (images: string[]) => {
    setForm(prev => ({ ...prev, images }));
  };

  const handleUploadImages = async (files: FileList) => {
    try {
      const uploadedUrls = await uploadImages(files);
      const newImages = [...form.images, ...uploadedUrls];
      handleImagesChange(newImages);
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleScheduleChange = (schedule: DisplaySchedule) => {
    setForm(prev => ({ ...prev, schedule }));
  };

  const handleSongsChange = (songs: DisplaySong[]) => {
    setForm(prev => ({ ...prev, songs }));
  };

  const handleCreateAnother = () => {
    setShowSuccessModal(false);
    setSuccessData(null);
    // Navigate to create a new display
    navigate('/display/edit');
  };

  const handleViewDisplay = () => {
    if (successData) {
      setShowSuccessModal(false);
      navigate(`/display/${successData.displayId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="container max-w-4xl pt-24 pb-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading display...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container max-w-4xl pt-24 pb-8">
        <h1 className="text-3xl font-bold mb-8">
          {id ? 'Edit Display' : 'Add Your Display'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Section */}
          <BasicInfoSection
            name={form.name}
            description={form.description}
            displayType={form.display_type}
            holidays={form.holidays}
            lightCount={form.light_count}
            projectorUsed={form.projector_used}
            illumibotUsed={form.illumibot_used}
            onNameChange={(value) => handleBasicInfoChange('name', value)}
            onDescriptionChange={(value) => handleBasicInfoChange('description', value)}
            onDisplayTypeChange={(value) => handleBasicInfoChange('display_type', value)}
            onHolidaysChange={(value) => handleBasicInfoChange('holidays', value)}
            onLightCountChange={(value) => handleBasicInfoChange('light_count', value)}
            onProjectorUsedChange={(value) => handleBasicInfoChange('projector_used', value)}
            onIllumibotUsedChange={(value) => handleBasicInfoChange('illumibot_used', value)}
          />

          {/* Location Section */}
          <LocationSection
            location={form.location}
            latitude={latitude}
            longitude={longitude}
            onLocationChange={handleLocationChange}
          />

          {/* Media Upload Section */}
          <MediaUploadSection
            images={form.images}
            uploadingImages={uploadingImages}
            onImagesChange={handleImagesChange}
            onUploadImages={handleUploadImages}
          />

          {/* Unified Schedule Section with Basic/Advanced Tabs */}
          <UnifiedScheduleSection
            schedule={form.schedule}
            city={city}
            state={state}
            onChange={handleScheduleChange}
            defaultTab="basic"
            showAdvanced={true}
          />

          {/* Songs Section */}
          <SongsSection
            songs={form.songs}
            onSongsChange={handleSongsChange}
          />

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                id ? 'Update Display' : 'Create Display'
              )}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
      
      {/* Success Modal */}
      {successData && (
        <DisplayUploadSuccessModal
          isOpen={showSuccessModal}
          displayTitle={successData.title}
          displayId={successData.displayId}
          onUploadAnother={handleCreateAnother}
          onViewDisplay={handleViewDisplay}
        />
      )}
    </div>
  );
};

export default EditDisplayForm;